// ============================================================
// MOONSHINE MASTERS — moonshineFolk3D.jsx        THE TOWNSFOLK AT THE BOARD (real Toonz bodies)
//
// caP'n, 2026-10-30: "get rid of the bodies at the campsite ... but i like the models being used in the contracts so you can
// see who it is. Use the unstable pierre toonz ... always use those first when making IE games."
// So the capsule stand-ins are gone; whoever is on the board stands beside it in their OWN body.
//
// THE LAW OF ONE BODY: the bodies are the Pier's Toonz kits, copied byte-identical (md5 = app-unstable-pierre = gamemaster/models):
//   ieTerrorSpecies.jsx (the 36 built species, TerrorSpecies)  ·  ieBeingModels.jsx (the twelve older lineages + Lords, IEBeing)
// Never edit those two here - re-copy when the Pier's move on. THEY OWN THE BODY, WE OWN THE POSE: this file only picks the body,
// sizes it to a person (the Pier's recruit heights), stands it on the ground and turns it to face the yard.
// A swap is one row in BODY_OF below. No new lights: a faint pool of the person's glow under their feet (the Pier's recruit trick).
// ============================================================
import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { IEBeing } from "./ieBeingModels";
import { TerrorSpecies, hasSpecies } from "./ieTerrorSpecies";

const HEIGHT = { beb: 1.35, champion: 1.7, lord: 2.05 };   // ⟵ DIAL  the Pier's recruit heights (a walker is ~1.5)

// toonzCast id -> [kit, being, tier]. The 36 KIT species are their own id in TerrorSpecies (beb); the older species and the
// Twelve Lords live in IEBeing (a Lord wears his Line's body at Lord tier). Anything unknown falls back to null (nobody drawn).
const LINE = {
  antz: ["antdroid", "beb"], rajaavi: ["spectral_tiger", "beb"], nyfaux: ["shadow_fox", "beb"], vyren: ["inverted_serpent", "beb"],
  xulan: ["xulan", "beb"], dragon: ["chaos_dragon", "beb"], dg0: ["dg0", "beb"], z4z: ["z4z", "beb"], h01: ["h01", "beb"], h09: ["h09", "beb"],
  saolesh: ["spectral_tiger", "lord"], xerathoun: ["chaos_dragon", "lord"], quan7: ["z4z", "lord"], osorun_veth: ["xulan", "lord"],
  raeyn_ao: ["shadow_fox", "lord"], ouraoth: ["inverted_serpent", "lord"], vyornhal: ["lucky_dragon", "lord"], aeodrith: ["premier_dragon", "lord"],
  calvarus: ["h01", "lord"], marylynn: ["h09", "lord"], daeogoz: ["dg0", "lord"], roztal: ["antdroid", "lord"],
};
export function bodyOf(id) {
  if (LINE[id]) return { kit: "lineage", being: LINE[id][0], tier: LINE[id][1] };
  if (hasSpecies(id)) return { kit: "species", being: id, tier: "beb" };
  return null;
}

let POOL = null;   // one soft radial texture, shared
function poolTex() {
  if (POOL) return POOL;
  const c = document.createElement("canvas"); c.width = c.height = 64;
  const g = c.getContext("2d"), r = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  r.addColorStop(0, "rgba(255,255,255,1)"); r.addColorStop(0.5, "rgba(255,255,255,0.35)"); r.addColorStop(1, "rgba(255,255,255,0)");
  g.fillStyle = r; g.fillRect(0, 0, 64, 64);
  POOL = new THREE.CanvasTexture(c); POOL.colorSpace = THREE.SRGBColorSpace;
  return POOL;
}

// one person: measured once, scaled to their tier's height, feet on the ground
export function ToonzPerson({ person, position, rotationY = 0 }) {
  const inner = useRef();
  const b = bodyOf(person.id);
  useLayoutEffect(() => {
    const g = inner.current;
    if (!g || !b) return;
    g.scale.setScalar(1); g.position.set(0, 0, 0); g.updateWorldMatrix(true, true);
    const box = new THREE.Box3().setFromObject(g);
    if (box.isEmpty()) return;
    const h = box.max.y - box.min.y, k = h > 0.05 ? HEIGHT[b.tier] / h : 1;
    g.scale.setScalar(k);
    g.position.y = -box.min.y * k;
  }, [b && b.kit, b && b.being, b && b.tier]);   // eslint-disable-line react-hooks/exhaustive-deps -- re-measure only when the body changes
  const pool = useMemo(() => new THREE.MeshBasicMaterial({
    map: poolTex(), color: person.glow || "#ffe9b0", transparent: true, opacity: 0.35, depthWrite: false, toneMapped: false, blending: THREE.AdditiveBlending,
  }), [person.glow]);
  useEffect(() => () => pool.dispose(), [pool]);
  if (!b) return null;
  return (
    <group position={position} rotation={[0, rotationY, 0]}>
      <group ref={inner}>{b.kit === "species" ? <TerrorSpecies being={b.being} tier={b.tier} /> : <IEBeing being={b.being} tier={b.tier} />}</group>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]} material={pool}><planeGeometry args={[2.2, 2.2]} /></mesh>
    </group>
  );
}
